import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { bindLegacyScoreTouch } from './legacy-score-touch';
import '../../styles/score-input.css';

type Props = {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label: string;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  minLabel?: string;
  maxLabel?: string;
  allowDecimal?: boolean;
};

/** A blank answer stays blank until the participant explicitly chooses a score. */
export default function ScoreInput({ value, onChange, label, min, max, step = 1, unit = '', minLabel, maxLabel, allowDecimal }: Props) {
  const id = useId();
  const rangeRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [draft, setDraft] = useState<{ text: string; value: number | undefined; error: string } | null>(null);
  const interval = step > 0 ? step : 1;
  const answered = typeof value === 'number' && Number.isFinite(value);
  const current = answered ? value : min;
  const activeDraft = draft && Object.is(draft.value, value) ? draft : null;
  const error = activeDraft?.error ?? '';
  const text = activeDraft ? activeDraft.text : answered ? String(value) : '';
  const progress = max > min ? Math.max(0, Math.min(100, (current - min) / (max - min) * 100)) : 0;
  const tickInterval = interval < 1 && Number.isInteger(min) && Number.isInteger(max) && max - min <= 15 ? 1 : interval;
  const count = Math.round((max - min) / tickInterval);
  const ticks = count > 0 && count <= 15
    ? Array.from({ length: count + 1 }, (_, i) => Number((min + i * tickInterval).toFixed(10))).filter(n => n <= max)
    : min === max ? [min] : [min, max];
  const choose = (n: number) => { setDraft(null); onChangeRef.current(n); };
  const chooseRef = useRef(choose);
  chooseRef.current = choose;

  useEffect(() => {
    const input = rangeRef.current;
    // Safari 12 predates Pointer Events and does not support touch-action: none.
    // Limit this fallback to the slider itself; the surrounding page stays scrollable.
    if (input && !('PointerEvent' in window)) return bindLegacyScoreTouch(input, n => chooseRef.current(n));
  }, [min, max, interval]);

  const commitMinimum = (input: HTMLInputElement) => {
    if (!answered) choose(Number(input.value));
  };
  const endpoint = (caption: string | undefined, n: number) => caption ? `${caption} (${n}${unit})` : `${n}${unit}`;

  return (
    <div className="score-control">
      <div className="score-entry">
        <label htmlFor={`${id}-number`}>分数{unit && ` (${unit})`}</label>
        <input
          id={`${id}-number`}
          className="input score-number"
          type="number"
          inputMode={allowDecimal === false || (Number.isInteger(interval) && Number.isInteger(min)) ? 'numeric' : 'decimal'}
          enterKeyHint="done"
          min={min}
          max={max}
          step={interval}
          value={text}
          placeholder="未选"
          aria-label={`${label}，输入分数`}
          aria-invalid={!!error}
          aria-describedby={`${id}-limits${error ? ` ${id}-error` : ''}`}
          onChange={(event) => {
            const input = event.currentTarget;
            const raw = input.value;
            const n = input.valueAsNumber;
            let message = '';
            if (input.validity.badInput) message = '请输入有效数字';
            else if (raw && (!Number.isFinite(n) || n < min || n > max)) message = `请输入 ${min} 至 ${max} 之间的分数`;
            else if (raw && allowDecimal === false && !Number.isInteger(n)) message = '请输入整数分数';
            else if (raw && input.validity.stepMismatch) message = `请从 ${min} 起按 ${interval} 的间隔输入`;
            const next = raw && !message ? n : undefined;
            input.setCustomValidity(message);
            setDraft({ text: raw, value: next, error: message });
            onChange(next);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
          }}
          ref={input => { if (input) input.setCustomValidity(error); }}
        />
      </div>
      <div className="score-scale">
        <div className="score-endpoints" id={`${id}-limits`}>
          <span>{endpoint(minLabel, min)}</span>
          <span>{endpoint(maxLabel, max)}</span>
        </div>
        <input
          ref={rangeRef}
          className="score-range"
          type="range"
          min={min}
          max={max}
          step={interval}
          value={current}
          aria-label={`${label}，滑动评分`}
          aria-valuetext={answered ? `${value}${unit}` : '未评分'}
          aria-describedby={`${id}-limits`}
          style={{ '--score-progress': `${progress}%` } as CSSProperties}
          onChange={event => choose(Number(event.currentTarget.value))}
          onPointerUp={event => { if (event.isPrimary) commitMinimum(event.currentTarget); }}
          onClick={event => commitMinimum(event.currentTarget)}
          onKeyUp={event => {
            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) commitMinimum(event.currentTarget);
          }}
        />
        <div className="score-ticks" aria-hidden="true">
          {ticks.map((n, i) => (
            <span key={n} className={i !== 0 && i !== ticks.length - 1 && i % Math.ceil((ticks.length - 1) / 3) !== 0 ? 'is-minor' : ''} style={{ left: `${max > min ? (n - min) / (max - min) * 100 : 0}%` }}>{n}</span>
          ))}
        </div>
      </div>
      {error && <div className="score-error" id={`${id}-error`} role="status">{error}</div>}
    </div>
  );
}

/** The runner has button-driven navigation, so validate optional numeric fields too. */
export function reportInvalidScore(): boolean {
  const fields = document.querySelectorAll<HTMLInputElement>('.runner .score-number');
  for (let i = 0; i < fields.length; i++) {
    if (!fields[i].checkValidity()) { fields[i].reportValidity(); fields[i].focus(); return true; }
  }
  return false;
}
